import React from 'react'
import { faFloppyDisk, FaIcon } from '@/lib/fa'
import { faClose } from '@fortawesome/pro-light-svg-icons'
import { toast } from '@/lib/toasts/api'

import { useLocalize } from '@/modules/shared/context/localize/LocalizeContext'
import { TripEventType } from '@/app/constants/delegation/tripEventType'
import useBusinessTripService from '@/modules/drivers/services/BusinessTripService'
import { useEditBusinessTrip } from '../context/EditBusinessTripContext'
import { useNavigateBackFromEditBusinessTrip } from './editBusinessTripNavigation'
import {
  toExchangeRateDate,
  type GeneratedAllowances,
} from '../hooks/useBusinessTripAllowancesSync'
import { useTripSaveIssues } from '../hooks/useTripSaveIssues'

const EditBusinesTripFooter = ({
  formData,
  companyId,
  driverId,
  setValidationErrors,
  isSaving,
  setIsSaving,
  validationErrors,
  tripId,
  flushPendingAllowances,
  onSaved,
}: {
  formData: any
  companyId: string
  driverId: string
  setValidationErrors: (e: Record<string, any>) => void
  isSaving: boolean
  setIsSaving: (v: boolean) => void
  validationErrors: Record<string, any>
  tripId: string
  flushPendingAllowances: () => Promise<GeneratedAllowances | null>
  onSaved: () => void
}) => {
  const { generateAllowances } = useBusinessTripService()
  const { saveTrip } = useTripSaveIssues()
  const { t } = useLocalize()
  const { showTachographView, showTmsView, showGpsView, showBordersView, showCardLoadsView } =
    useEditBusinessTrip()
  const navigateBack = useNavigateBackFromEditBusinessTrip()

  const validateFormData = (): boolean => {
    const errors: Record<string, any> = {}

    // Validate ribbon fields
    if (!formData.number || formData.number.trim() === '') {
      errors.number = t('newDelegation.validation-delegation-number')
    }

    if (!formData.destinationCountry) {
      errors.destinationCountry = t(
        'newDelegation.validation-destination-country',
      )
    }

    // Validate events
    if (!formData.events || formData.events.length === 0) {
      errors.events = t('newDelegation.events-validation-events-missing')
    }

    // Validate that events have Begin and Finish
    if (formData.events && formData.events.length > 0) {
      const hasBegin = formData.events.some(
        (e: any) =>
          e.tripEvent === TripEventType.Begin ||
          e.tripEvent === 'Begin' ||
          e.tripEvent === 1,
      )
      const hasFinish = formData.events.some(
        (e: any) =>
          e.tripEvent === TripEventType.Finish ||
          e.tripEvent === 'Finish' ||
          e.tripEvent === 2,
      )
      if (!hasBegin || !hasFinish) {
        errors.events = t(
          'newDelegation.events-validation-events-start-or-end-missing',
        )
      }
    }

    setValidationErrors(errors)
    return Object.keys(errors).length === 0
  }
  const handleCancel = () => {
    navigateBack()
  }
  const handleSave = async () => {
    if (!validateFormData()) {
      toast.error({
        text: t('newDelegation.validation-error'),
      })

      // Scroll to first error field
      const firstErrorKey = Object.keys(validationErrors)[0]
      if (
        firstErrorKey === 'number' ||
        firstErrorKey === 'destinationCountry'
      ) {
        // Scroll to ribbon
        const ribbonElement = document.querySelector('[data-ribbon]')
        if (ribbonElement) {
          ribbonElement.scrollIntoView({ behavior: 'smooth', block: 'center' })
        }
      } else if (firstErrorKey === 'events') {
        // Scroll to events tab
        const eventsTab = document.querySelector('[data-tab="events"]')
        if (eventsTab) {
          eventsTab.scrollIntoView({ behavior: 'smooth', block: 'center' })
        }
      }

      return
    }

    try {
      setIsSaving(true)

      // An event edit schedules a recalculation of the diets and the auto-generated
      // costs; run whatever is still debounced or in flight first, so the save
      // persists the numbers the user is looking at instead of the stale ones.
      const recalculated = await flushPendingAllowances()

      // Prepare data for saving - ensure all required fields are included
      const saveData = {
        ...formData,
        companyId,
        driverId,
        id: tripId,
        // Map destinationCountry to country field expected by the backend
        country: formData.destinationCountry || formData.country,
        // Ensure arrays are properly formatted
        events: formData.events || [],
        allowances: recalculated?.allowances || formData.allowances || [],
        accomodations: formData.accommodations || formData.accomodations || [],
        invoices: recalculated?.invoices || formData.invoices || [],
        advancePayments: formData.advancePayments || [],
        alimentations: formData.alimentations || [],
      }

      // Refresh the exchange rates for what is about to be saved — but only when
      // nothing was recalculated a moment ago, since that pass already fetched
      // them for the same exchangeRateDate. Cost invoices are preserved here:
      // an amount the user edited by hand must survive the save (#2191).
      try {
        const generated = recalculated
          ? null
          : await generateAllowances({
              companyId,
              driverId,
              exchangeRateDate: toExchangeRateDate(formData.exchangeRateDate),
              country: formData.destinationCountry || null,
              events: saveData.events,
              alimentations: saveData.alimentations,
              invoices: saveData.invoices,
              accomodations: saveData.accomodations,
              stopDownloadingRates: formData.stopDownloadingRates || false,
              preserveCostInvoices: true,
            })

        if (generated) {
          saveData.allowances =
            generated.Allowances || generated.allowances || saveData.allowances
          saveData.invoices =
            generated.Invoices || generated.invoices || saveData.invoices
        }
      } catch (generateErr: any) {
        console.error('Error generating allowances before save:', generateErr)
        toast.error({
          text:
            generateErr?.response?.data?.message ||
            t('newDelegation.delegation-edit-failed-details'),
        })
        return
      }

      // The server answers with OperationResultModel[] either as a 200 body or,
      // when it refuses the save, as the body of a 400 — so both have to be
      // unpacked the same way. Retry once with force when the user accepts the
      // warnings, mirroring "Yes" in WPF's Yes/No dialogs.
      const saved = await saveTrip({ companyId, tripId, data: saveData })
      if (!saved) return

      toast.success({
        text: t('newDelegation.delegation-edited-title'),
      })
      onSaved()
      navigateBack({ saved: true })
    } catch (err: any) {
      console.error('Error saving business trip:', err)
      toast.error({
        text:
          err?.response?.data?.message ||
          t('newDelegation.delegation-edit-failed-details'),
      })
    } finally {
      setIsSaving(false)
    }
  }

  if (showTachographView || showTmsView || showGpsView || showBordersView || showCardLoadsView) {
    return null
  }

  return (
    <div className="tw-flex tw-items-center tw-justify-end tw-gap-3 tw-bg-white tw-p-4">
      <button
        onClick={handleCancel}
        className="hover:tw-bg-base-50 tw-flex tw-items-center tw-gap-2 tw-rounded-lg tw-border tw-border-base-300 tw-bg-white tw-px-4 tw-py-2 tw-text-sm tw-font-medium tw-text-content-base"
      >
        <FaIcon icon={faClose} className="tw-h-4 tw-w-4" />
        {t('common.cancel')}
      </button>
      <button
        onClick={handleSave}
        disabled={isSaving}
        className="tw-flex tw-items-center tw-gap-2 tw-rounded-lg tw-bg-primary tw-px-4 tw-py-2 tw-text-sm tw-font-medium tw-text-white hover:tw-bg-primary/90 disabled:tw-opacity-50"
      >
        <FaIcon icon={faFloppyDisk} className="tw-h-4 tw-w-4" />
        {isSaving ? t('driverModule.saving') : t('common.save')}
      </button>
    </div>
  )
}

export default EditBusinesTripFooter
